<template>
  <div>
    <v-form>
      <v-row>
        <v-col md="6" cols="12" v-if="context !== 'create'">
          <v-text-field
            v-model="localReport.name"
            :label="$tc('name')"
            prepend-inner-icon="mdi-form-textbox" />
        </v-col>
        <v-col>
          <v-select v-model="selectedPercentViolating" :items="percentViolatingOptions" :label="$t('reports_ui.vehicle_violation_report.percent_violating')"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <vf-autocomplete v-model="selectedViolations" :items="violationOptions" :label="$tc('violation', 2)"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="d-flex align-center">
            <date-picker
              v-model="startDate"
              :timezone="timezone"
              :output-moment="true"
              :label="$t('start_date')"
              prepend-inner-icon="mdi-calendar"
              :error-messages="dateRangeErrors"
              :reset-on-timezone-change="context === 'create'"/>
            <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
            <date-picker
              v-model="endDate"
              :timezone="timezone"
              :output-moment="true"
              :end-of-day="true"
              :label="$t('end_date')"
              prepend-inner-icon="mdi-calendar"
              :error="dateRangeErrors.length > 0"
              :reset-on-timezone-change="context === 'create'"/>
          </div>
        </v-col>
      </v-row>
      <date-selector-shortcuts :lowerBound.sync="startDate" :upperBound.sync="endDate" :timezone="timezone"/>
    </v-form>
  </div>
</template>

<script>

import VfAutocomplete from '@/components/shared/VfAutocomplete'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import DatePicker from '@/components/shared/DatePicker'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import DateSelectorShortcuts from '@/components/report/DateSelectorShortcuts'

import ReportUtils from '@/components/report/ReportUtils'
import { filter, get, map, snakeCase } from 'lodash'

export default {
  name: 'VehicleViolationReportForm',
  components: {
    VfAutocomplete,
    DatePicker,
    DateSelectorShortcuts
  },
  mixins: [
    CurrentUserMixin,
    ReportFormMixin,
    ValidationMixin
  ],
  validations: {
    dateRange: {
      rangeIsValid: function () {
        return ReportUtils.dateRangeIsValid(this.startDate, this.endDate)
      }
    }
  },
  data () {
    return {
      reportType: 'VehicleViolationReport',
      availableViolations: {},
      selectedViolations: [],
      percentViolatingOptions: [
        {
          value: 25,
          text: '>= 25%'
        },
        {
          value: 50,
          text: '>= 50%'
        },
        {
          value: 75,
          text: '>= 75%'
        }
      ],
      selectedPercentViolating: null,
      startDate: null,
      endDate: null
    }
  },
  methods: {
    defaultReportName () {
      return this.$t('reports_ui.vehicle_violation_report.name')
    },
    /** Hook to populate report (called from mixin) */
    populateReportForm () {
      this.fetchViolations()
      this.preSelectPercentage()
      this.preSelectDates()
    },

    /** Build report params (called from mixin) */
    buildReportParameters () {
      const reportParameters = {}
      reportParameters.violations = this.selectedViolations
      reportParameters.percentage = this.selectedPercentViolating
      reportParameters.startDate = this.startDate.format()
      reportParameters.endDate = this.endDate.format()
      reportParameters.timezone = this.timezone

      reportParameters.customer_id = get(this.localReport, 'parameters.customer_id') || this.customerContext.id
      return reportParameters
    },

    /**
     * Using a previous report, find all selected violations and set the state of this component to match, default to
     * selecting all violations.
     *
     * @return undefined
     */
    preSelectViolations () {
      const preSelectedViolations = get(this, 'report.parameters.violations')
      if (preSelectedViolations) {
        this.selectedViolations = preSelectedViolations
      } else {
        this.selectedViolations = map(this.violationOptions, x => x.value)
      }
    },
    /**
     * Pull the percentage selected from a previous report (if defined) and set the state of this form to match.
     *
     * @return undefined
     */
    preSelectPercentage () {
      const preSelectedPercentage = get(this, 'report.parameters.percentage')
      if (preSelectedPercentage) {
        this.selectedPercentViolating = preSelectedPercentage
      }
    },
    /**
     * Fetch all known violation types from the back-end and update this form with those results. When results are
     * received update the form state to pre-select all violations or all violations on a previous report if using
     * a previous report as a template.
     *
     * @var ReportRecord report
     * @return undefined
     */
    fetchViolations () {
      this.$orm('RouteViolationType')
        .findAll({})
        .then((collection) => {
          this.availableViolations = filter(collection.getItems(), (routeViolationType) => {
            const typesToExclude = [
              'TYPE_MISSED_STOP',
              'TYPE_SET_POINT_NOT_REACHED',
              'TYPE_NOMINAL_AIR_TEMP_NOT_REACHED',
              'TYPE_SWAPPED_AIR_SENSORS'
            ]
            return typesToExclude.indexOf(routeViolationType.id) === -1
          })
          this.preSelectViolations()
        })
        .catch(error => {
          this.handleError(error)
        })
    }
  },
  computed: {
    /**
     * Returns an array of available violations formatted for use in the autocomplete component.
     *
     * @return array
     */
    violationOptions () {
      return map(this.availableViolations, (violation) => {
        return {
          text: this.$tc(snakeCase(violation.name)),
          value: violation.name
        }
      })
    },
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    }
  }
}

</script>
